/** Shared domain types. The compiler forbids enums; these unions are the law. */

export const THRESHOLD_STATES = ['threshold_reached', 'threshold_not_reached', 'pending'] as const;
export type ThresholdState = (typeof THRESHOLD_STATES)[number];

export const EXPERIMENT_STATUSES = ['draft', 'running', 'decided', 'archived'] as const;
export type ExperimentStatus = (typeof EXPERIMENT_STATUSES)[number];

export const SUCCESS_EVENT_KINDS = [
  'signup',
  'waitlist',
  'purchase',
  'reply',
  'conversation',
  'custom',
] as const;
export type SuccessEventKind = (typeof SUCCESS_EVENT_KINDS)[number];

export const EVENT_KINDS = [
  'link_click',
  'impression',
  'like',
  'signup',
  'waitlist',
  'purchase',
  'reply',
  'conversation',
  'custom',
  'refund',
  'dispute',
  'trial_started',
] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

/** Written only by the Creem webhook. Manual POST cannot forge these. */
export const WEBHOOK_ONLY_KINDS: ReadonlySet<EventKind> = new Set([
  'refund',
  'dispute',
  'trial_started',
]);

export const MANUAL_EVENT_KINDS = EVENT_KINDS.filter(
  (kind) => kind !== 'link_click' && !WEBHOOK_ONLY_KINDS.has(kind),
);

/** Kinds that never count as demand, even when the count is large. */
export const VANITY_KINDS: ReadonlySet<EventKind> = new Set(['impression', 'like']);

/** Kinds that can count as a response to the offer. */
export const DEMAND_KINDS: ReadonlySet<EventKind> = new Set([
  'signup',
  'waitlist',
  'purchase',
  'reply',
  'conversation',
  'custom',
]);

export const UA_CLASSES = ['browser', 'bot', 'unknown'] as const;
export type UaClass = (typeof UA_CLASSES)[number];

export const EVENT_SOURCES = ['link', 'manual', 'creem'] as const;
export type EventSource = (typeof EVENT_SOURCES)[number];

export const RULE_IDS = [
  'low_activity',
  'vanity_only',
  'threshold_reached',
  'clicks_no_conversion',
  'no_clicks',
  'threshold_not_reached',
] as const;
export type RuleId = (typeof RULE_IDS)[number];

/** Count-based success only. Rates inform; they never gate. */
export const LOW_ACTIVITY_EVENT_FLOOR = 20;
export const LOW_ACTIVITY_DAY_FLOOR = 3;
export const RATIO_DIRECTIONAL_BELOW = 30;

export type EvidenceItem = {
  label: string;
  value: string;
};

export type Ratio = {
  numerator: number;
  denominator: number;
  /** True when the denominator is too small to treat the ratio as a rate. */
  directional: boolean;
};

export type Funnel = {
  impressions: number;
  likes: number;
  clicks: number;
  conversions: number;
  otherDemand: number;
  notes: number;
  totalEvents: number;
  daysElapsed: number;
  clickThrough: Ratio | null;
  conversionRate: Ratio | null;
};

export type Recommendation = {
  ruleId: RuleId;
  thresholdState: ThresholdState;
  summary: string;
  nextStep: string;
  evidence: EvidenceItem[];
};

export type Experiment = {
  id: string;
  user_id: string;
  title: string;
  audience: string;
  message: string;
  offer: string;
  channel: string;
  success_event_kind: SuccessEventKind;
  success_label: string;
  success_threshold: number;
  status: ExperimentStatus;
  started_at: string | null;
  created_at: string;
  updated_at: string;
};

export type TrackedLink = {
  id: string;
  user_id: string;
  experiment_id: string;
  slug: string;
  destination_url: string;
  label: string;
  created_at: string;
};

export type TrackedEvent = {
  id: string;
  user_id: string;
  experiment_id: string;
  link_id: string | null;
  kind: EventKind;
  source: string;
  quantity: number;
  occurred_at: string;
  visitor_hash: string | null;
  ua_class: UaClass | null;
  referrer_host: string | null;
  note: string;
  creem_event_id: string | null;
  creem_checkout_id: string | null;
  creem_subscription_id: string | null;
  creem_request_id: string | null;
  creem_order_id: string | null;
  creem_transaction_id: string | null;
  created_at: string;
};

export type Note = {
  id: string;
  user_id: string;
  experiment_id: string;
  body: string;
  created_at: string;
};

export type Decision = {
  id: string;
  user_id: string;
  experiment_id: string;
  rule_id: RuleId;
  threshold_state: ThresholdState;
  summary: string;
  next_step: string;
  evidence: EvidenceItem[];
  edited: boolean;
  created_at: string;
};

export const SUCCESS_KIND_LABELS: Record<SuccessEventKind, string> = {
  signup: 'Signups',
  waitlist: 'Waitlist joins',
  purchase: 'Purchases',
  reply: 'Replies',
  conversation: 'Conversations',
  custom: 'Custom count',
};

export function isThresholdState(value: string): value is ThresholdState {
  return (THRESHOLD_STATES as readonly string[]).includes(value);
}

export function isEventKind(value: string): value is EventKind {
  return (EVENT_KINDS as readonly string[]).includes(value);
}

export function isManualEventKind(value: string): value is EventKind {
  return isEventKind(value) && (MANUAL_EVENT_KINDS as readonly string[]).includes(value);
}

export function isEventSource(value: string): value is EventSource {
  return (EVENT_SOURCES as readonly string[]).includes(value);
}

export function isSuccessEventKind(value: string): value is SuccessEventKind {
  return (SUCCESS_EVENT_KINDS as readonly string[]).includes(value);
}

export function isExperimentStatus(value: string): value is ExperimentStatus {
  return (EXPERIMENT_STATUSES as readonly string[]).includes(value);
}

export function conversionLabel(kind: SuccessEventKind, customLabel: string): string {
  if (kind === 'custom' && customLabel.trim()) return customLabel.trim();
  return SUCCESS_KIND_LABELS[kind];
}

/** Forbidden phrases. Tests lock these so copy cannot drift into PMF or verdicts. */
export const FORBIDDEN_CONCLUSION_PHRASES = [
  'product-market fit',
  'product market fit',
  'the idea is invalid',
  'idea is invalid',
  'validated the idea',
  'invalid idea',
] as const;
